import React from "react";
import { useTranslations } from "use-intl";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import DocumentViewerPanel from "@/components/shared/DocumentViewerPanel";
import SekEquivalent from "@/components/shared/SekEquivalent";
import { formatCurrency } from "@/utils/paymentUtils";
import { useFormatters } from "@financialharmony/ui";
import { Invoice } from "@/types/payments";

interface CompletedDocumentDialogProps {
  document: Invoice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// The document types with a catalog label (`consultantUpload.documentTypes`); any other shows its own name.
const LABELLED_TYPES = ["payable", "receivable", "receipt", "expense", "sales_report"] as const;
type LabelledType = (typeof LABELLED_TYPES)[number];

function isLabelledType(type: string): type is LabelledType {
  return (LABELLED_TYPES as readonly string[]).includes(type);
}

const CompletedDocumentDialog: React.FC<CompletedDocumentDialogProps> = ({
  document,
  open,
  onOpenChange,
}) => {
  const t = useTranslations("consultantUpload");
  const { formatDate } = useFormatters();
  if (!document) return null;

  const formatOptionalDate = (dateStr?: string | null) =>
    dateStr && dateStr.trim() ? formatDate(dateStr, "numeric") : "—";

  const documentType = document.documentType ?? null;
  let typeLabel = t("completed.unknown");
  if (documentType) typeLabel = isLabelledType(documentType) ? t(`documentTypes.${documentType}`) : documentType;

  const detailContent = (
    <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
      <div>
        <p className="text-xs text-muted-foreground">{t("completed.type")}</p>
        <Badge variant="outline">{typeLabel}</Badge>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{t("completed.vendor")}</p>
        <p className="font-medium">{document.vendor || "—"}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{t("completed.amount")}</p>
        <p className="font-medium">
          {document.amount != null
            ? formatCurrency(document.amount, document.currency || "SEK")
            : "—"}
        </p>
        <SekEquivalent sekAmount={document.sekAmount} currency={document.currency || "SEK"} />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{t("completed.date")}</p>
        <p className="font-medium">
          {formatOptionalDate(document.transactionDate ?? document.dueDate)}
        </p>
      </div>
      {document.remittanceInformation && (
        <div className="col-span-2">
          <p className="text-xs text-muted-foreground">{t("completed.reference")}</p>
          <p className="font-medium">{document.remittanceInformation}</p>
        </div>
      )}
      {document.vendorAccount && (
        <div className="col-span-2">
          <p className="text-xs text-muted-foreground">{t("completed.vendorAccount")}</p>
          <p className="font-mono text-sm">{document.vendorAccount}</p>
        </div>
      )}
      {document.expenseCategory && (
        <div>
          <p className="text-xs text-muted-foreground">{t("completed.category")}</p>
          <p className="font-medium capitalize">{document.expenseCategory}</p>
        </div>
      )}
      {document.paymentMethod && (
        <div>
          <p className="text-xs text-muted-foreground">{t("completed.paymentMethod")}</p>
          <p className="font-medium capitalize">{document.paymentMethod.replace("_", " ")}</p>
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          document.filePath
            ? "max-w-5xl h-[90vh] flex flex-col"
            : "max-w-lg max-h-[90vh] overflow-y-auto"
        }
      >
        <DialogHeader>
          <DialogTitle>{document.fileName || t("completed.document")}</DialogTitle>
          <DialogDescription>
            {typeLabel} · {document.vendor || "—"}
          </DialogDescription>
        </DialogHeader>

        {document.filePath ? (
          <ResizablePanelGroup direction="horizontal" className="flex-1 mt-4">
            <ResizablePanel defaultSize={50} minSize={25}>
              <DocumentViewerPanel
                filePath={document.filePath}
                fileName={document.fileName ?? undefined}
              />
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={50} minSize={30} className="pl-4">
              <div className="h-full overflow-y-auto pb-4">
                {detailContent}
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          <div className="mt-4">{detailContent}</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompletedDocumentDialog;